import { useCallback, useEffect, useRef, useState } from 'react'

// =============================================================================
// Types
// =============================================================================

/**
 * Configuration for a single column
 */
export interface ColumnConfig {
  /** Unique identifier for the column */
  id: string
  /** Initial width as a percentage (0-100) */
  initialWidth: number
  /** Minimum width as a percentage (default: 10) */
  minWidth?: number
  /** Maximum width as a percentage (default: 80) */
  maxWidth?: number
}

/**
 * Configuration for the resizable columns hook
 */
export interface UseResizableColumnsConfig {
  /** Column configurations in order from left to right */
  columns: ColumnConfig[]
  /** Callback when column widths change */
  onWidthsChange?: (widths: Record<string, number>) => void
}

/**
 * Props for the resize handle component
 */
export interface ResizeHandleProps {
  /**
   * Pointer down handler to start dragging. Pointer events cover mouse,
   * touch, and pen; give the handle `touch-action: none` (`touch-none`)
   * so touch drags resize instead of scrolling.
   */
  onPointerDown: (e: React.PointerEvent) => void
  /** Keyboard handler for arrow key resizing */
  onKeyDown: (e: React.KeyboardEvent) => void
}

/**
 * Return type for the useResizableColumns hook
 */
export interface UseResizableColumnsReturn {
  /** Current column widths as percentages, keyed by column id */
  widths: Record<string, number>
  /** CSS grid-template-columns value */
  gridTemplate: string
  /** Get props for a resize handle between two columns */
  getResizeHandleProps: (leftColumnId: string) => ResizeHandleProps
  /** Container ref to attach to the resizable area */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Reset to initial widths */
  reset: () => void
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for managing resizable column widths.
 *
 * Provides mouse drag and keyboard (arrow keys) support for resizing columns.
 * Columns are resized as percentages that always sum to 100%.
 *
 * @example
 * ```tsx
 * const { widths, gridTemplate, getResizeHandleProps, containerRef } = useResizableColumns({
 *   columns: [
 *     { id: 'name', initialWidth: 40 },
 *     { id: 'expected', initialWidth: 30 },
 *     { id: 'actual', initialWidth: 30 },
 *   ],
 * })
 *
 * return (
 *   <div ref={containerRef} style={{ gridTemplateColumns: gridTemplate }}>
 *     <div>Name</div>
 *     <div style={{ position: 'relative' }}>
 *       <div className="resize-handle" {...getResizeHandleProps('name')} />
 *       Expected
 *     </div>
 *     <div style={{ position: 'relative' }}>
 *       <div className="resize-handle" {...getResizeHandleProps('expected')} />
 *       Actual
 *     </div>
 *   </div>
 * )
 * ```
 */
export function useResizableColumns(
  config: UseResizableColumnsConfig,
): UseResizableColumnsReturn {
  const { columns, onWidthsChange } = config

  // Build initial widths from config
  const buildInitialWidths = useCallback((): Record<string, number> => {
    const widths: Record<string, number> = {}
    for (const col of columns) {
      widths[col.id] = col.initialWidth
    }
    return widths
  }, [columns])

  // State for current column widths
  const [widths, setWidths] =
    useState<Record<string, number>>(buildInitialWidths)

  // Refs for drag state
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isDraggingRef = useRef<string | null>(null) // Column id of the LEFT column of the divider
  const startXRef = useRef(0)
  const startWidthsRef = useRef(widths)

  // Refs for cleanup - store active handlers to remove on unmount
  const cleanupRef = useRef<(() => void) | null>(null)

  // Get column config by id
  const getColumnConfig = useCallback(
    (id: string): ColumnConfig | undefined => {
      return columns.find((col) => col.id === id)
    },
    [columns],
  )

  // Get next column after a given column id
  const getNextColumn = useCallback(
    (id: string): ColumnConfig | undefined => {
      const index = columns.findIndex((col) => col.id === id)
      if (index === -1 || index === columns.length - 1) return undefined
      return columns[index + 1]
    },
    [columns],
  )

  // Update widths and notify
  const updateWidths = useCallback(
    (newWidths: Record<string, number>) => {
      setWidths(newWidths)
      onWidthsChange?.(newWidths)
    },
    [onWidthsChange],
  )

  // Handle pointer down on a resize divider (mouse, touch, or pen)
  const handlePointerDown = useCallback(
    (leftColumnId: string) => (e: React.PointerEvent) => {
      e.preventDefault()

      const leftCol = getColumnConfig(leftColumnId)
      const rightCol = getNextColumn(leftColumnId)
      if (!leftCol || !rightCol) return

      isDraggingRef.current = leftColumnId
      startXRef.current = e.clientX
      startWidthsRef.current = { ...widths }

      // Keep receiving moves even when the pointer leaves the handle
      // (also what routes touch moves here instead of scrolling the page,
      // together with touch-action: none on the handle).
      const handle = e.currentTarget
      if (handle instanceof Element) {
        try {
          handle.setPointerCapture(e.pointerId)
        } catch {
          // Capture is best-effort; document listeners still track the drag.
        }
      }

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (!isDraggingRef.current || !containerRef.current) return

        const containerWidth = containerRef.current.offsetWidth
        const deltaX = moveEvent.clientX - startXRef.current
        const deltaPercent = (deltaX / containerWidth) * 100

        const leftConfig = getColumnConfig(isDraggingRef.current)
        const rightConfig = getNextColumn(isDraggingRef.current)
        if (!leftConfig || !rightConfig) return

        const leftMin = leftConfig.minWidth ?? 10
        const leftMax = leftConfig.maxWidth ?? 80
        const rightMin = rightConfig.minWidth ?? 10
        const rightMax = rightConfig.maxWidth ?? 80

        const startLeft = startWidthsRef.current[leftConfig.id]
        const startRight = startWidthsRef.current[rightConfig.id]

        // Calculate new widths
        const newLeft = clamp(startLeft + deltaPercent, leftMin, leftMax)
        const newRight = clamp(startRight - deltaPercent, rightMin, rightMax)

        // Verify total is reasonable (account for floating point)
        const otherWidths = Object.entries(startWidthsRef.current)
          .filter(([id]) => id !== leftConfig.id && id !== rightConfig.id)
          .reduce((sum, [, w]) => sum + w, 0)

        const total = otherWidths + newLeft + newRight
        if (total > 95 && total < 105) {
          updateWidths({
            ...startWidthsRef.current,
            [leftConfig.id]: newLeft,
            [rightConfig.id]: newRight,
          })
        }
      }

      const cleanup = () => {
        isDraggingRef.current = null
        cleanupRef.current = null
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerEnd)
        document.removeEventListener('pointercancel', handlePointerEnd)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      const handlePointerEnd = () => {
        cleanup()
      }

      // Store cleanup for unmount handling
      cleanupRef.current = cleanup

      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerEnd)
      document.addEventListener('pointercancel', handlePointerEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [widths, getColumnConfig, getNextColumn, updateWidths],
  )

  // Cleanup on unmount - handles case where component unmounts mid-drag
  useEffect(() => {
    return () => {
      cleanupRef.current?.()
    }
  }, [])

  // Handle keyboard navigation for accessibility
  const handleKeyDown = useCallback(
    (leftColumnId: string) => (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

      e.preventDefault()

      const leftCol = getColumnConfig(leftColumnId)
      const rightCol = getNextColumn(leftColumnId)
      if (!leftCol || !rightCol) return

      const delta = e.key === 'ArrowLeft' ? -2 : 2

      const leftMin = leftCol.minWidth ?? 10
      const leftMax = leftCol.maxWidth ?? 80
      const rightMin = rightCol.minWidth ?? 10
      const rightMax = rightCol.maxWidth ?? 80

      const newLeft = clamp(widths[leftCol.id] + delta, leftMin, leftMax)
      const newRight = clamp(widths[rightCol.id] - delta, rightMin, rightMax)

      updateWidths({
        ...widths,
        [leftCol.id]: newLeft,
        [rightCol.id]: newRight,
      })
    },
    [widths, getColumnConfig, getNextColumn, updateWidths],
  )

  // Get props for a resize handle
  const getResizeHandleProps = useCallback(
    (leftColumnId: string): ResizeHandleProps => ({
      onPointerDown: handlePointerDown(leftColumnId),
      onKeyDown: handleKeyDown(leftColumnId),
    }),
    [handlePointerDown, handleKeyDown],
  )

  // Generate grid-template-columns CSS value
  const gridTemplate = columns.map((col) => `${widths[col.id]}%`).join(' ')

  // Reset to initial widths
  const reset = useCallback(() => {
    updateWidths(buildInitialWidths())
  }, [buildInitialWidths, updateWidths])

  return {
    widths,
    gridTemplate,
    getResizeHandleProps,
    containerRef,
    reset,
  }
}
